import { useEffect, useMemo, useState } from 'react';
import { AnimatePresence } from 'framer-motion';
import { X, Boxes, Plus, Trash2, Wrench, Lock, Clock } from 'lucide-react';
import { ModalRoot } from '@/components/ui/ModalRoot';
import { Z_MODALS } from '@/lib/zIndex';
import { useGameStore } from '@/state/gameStore';
import { VEHICLE_SPECS } from '@/data/vehicles';
import { stationAllowsRepair } from '@/data/stations';
import {
  modulesForType,
  moduleById,
  usedSlotsForInstalls,
  type LegacyModuleShape as VehicleModule,
} from '@/data/vehicleModules';
import {
  MODULE_SWAP_DURATION_MS,
  MODULE_SWAP_SURCHARGE,
  MODULE_SWAP_REFUND_RATE,
} from '@/state/gameStore';
import { formatEuro } from '@/lib/format';
import type { Vehicle } from '@/state/types';

interface ModuleConfigDialogProps {
  vehicle: Vehicle | null;
  onClose: () => void;
}

const CATEGORY_LABEL: Record<VehicleModule['category'], string> = {
  water: 'Wasser',
  breathing: 'Atemschutz',
  rescue: 'Technische Rettung',
  special: 'Spezial',
  medical: 'Medizinisch',
  police: 'Polizei',
};

/**
 * Phase 7.2.5 — Module configuration dialog. Phase 12.3.2 — re-architected
 * around the workshop-swap workflow:
 *
 *   - Vehicle must be at base (Status 2) AND host station must allow repair.
 *   - Player accumulates pending Add/Remove operations locally.
 *   - "Tausch starten" books all of them in a single 5-minute Werkstatt-Aufenthalt.
 *   - During the swap: vehicle is in Status 6, can't be dispatched, with a
 *     countdown + "Tausch abbrechen" (50 % refund).
 *   - On commit: queued modules are installed, queued removes are uninstalled.
 *
 * Pricing:
 *   - Add: spec.cost × (1 + 50 %) surcharge.
 *   - Remove (non-default): refund spec.cost × 30 %.
 *   - Remove (default): no refund (the default came free with the vehicle).
 *   - Cancel mid-swap: 50 % of the net cost paid.
 */
export function ModuleConfigDialog({ vehicle, onClose }: ModuleConfigDialogProps) {
  const credits = useGameStore((s) => s.credits);
  const mode = useGameStore((s) => s.mode);
  const stations = useGameStore((s) => s.stations);
  const startModuleSwap = useGameStore((s) => s.startModuleSwap);
  const cancelModuleSwap = useGameStore((s) => s.cancelModuleSwap);
  const pushToast = useGameStore((s) => s.pushToast);
  const gameTime = useGameStore((s) => s.gameTime);

  const [pendingAddIds, setPendingAddIds] = useState<string[]>([]);
  const [pendingRemoveIds, setPendingRemoveIds] = useState<string[]>([]);

  useEffect(() => {
    if (!vehicle) return;
    setPendingAddIds([]);
    setPendingRemoveIds([]);
  }, [vehicle?.id]);

  const compatible = useMemo(
    () => (vehicle ? modulesForType(vehicle.type) : []),
    [vehicle],
  );

  const station = useMemo(
    () => (vehicle ? stations.find((s) => s.id === vehicle.stationId) : null),
    [vehicle, stations],
  );

  if (!vehicle) return null;

  const spec = VEHICLE_SPECS[vehicle.type];
  const installed = vehicle.installedModules ?? [];
  const allInstalledIds = installed.map((i) => i.moduleId);

  // Effective post-swap module set for slot accounting.
  const removeSet = new Set(pendingRemoveIds);
  const remainingInstalls = installed.filter((i) => !removeSet.has(i.id));
  const projectedIds = [
    ...remainingInstalls.map((i) => i.moduleId),
    ...pendingAddIds,
  ];
  const usedSlots = usedSlotsForInstalls(projectedIds);
  const totalSlots = spec.moduleSlots;
  const free = Math.max(0, totalSlots - usedSlots);
  // Module families already represented in installed (current OR projected).
  const projectedFamilies = new Set(allInstalledIds);
  for (const id of pendingAddIds) projectedFamilies.add(id);

  const atBase = vehicle.status === 2;
  const inWorkshop = Boolean(vehicle.workshopUntilAt && vehicle.pendingModuleSwap);
  const hasWorkshop = station ? stationAllowsRepair(station) : false;

  // Pricing calculation for pending swap.
  const addCostGross = pendingAddIds.reduce((acc, id) => {
    const m = moduleById(id);
    return acc + (m?.cost ?? 0);
  }, 0);
  const addCostWithSurcharge = Math.round(
    addCostGross * (1 + MODULE_SWAP_SURCHARGE),
  );
  const removeRefund = pendingRemoveIds.reduce((acc, instId) => {
    const inst = installed.find((i) => i.id === instId);
    if (!inst || inst.isDefault) return acc;
    const m = moduleById(inst.moduleId);
    return acc + Math.round((m?.cost ?? 0) * MODULE_SWAP_REFUND_RATE);
  }, 0);
  const netCost = Math.max(0, addCostWithSurcharge - removeRefund);
  const hasChanges = pendingAddIds.length > 0 || pendingRemoveIds.length > 0;

  const handleStart = () => {
    if (!atBase) {
      pushToast({ kind: 'error', text: 'Modul-Wechsel nur an der Wache möglich.' });
      return;
    }
    if (!hasWorkshop) {
      pushToast({
        kind: 'error',
        text: 'Erfordert Werkstatt-Erweiterung an dieser Wache.',
      });
      return;
    }
    if (mode !== 'sandbox' && credits < netCost) {
      pushToast({ kind: 'error', text: `Nicht genug Credits (${formatEuro(netCost)}).` });
      return;
    }
    const ok = startModuleSwap(vehicle.id, pendingAddIds, pendingRemoveIds);
    if (!ok) {
      pushToast({ kind: 'error', text: 'Modul-Tausch konnte nicht gestartet werden.' });
      return;
    }
    pushToast({
      kind: 'success',
      text: `Modul-Tausch begonnen. ${vehicle.displayName ?? spec.label} ist 5 Min außer Dienst.`,
    });
    onClose();
  };

  const handleCancel = () => {
    const ok = cancelModuleSwap(vehicle.id);
    if (ok) onClose();
  };

  // Group compatible modules by category
  const byCategory = compatible.reduce(
    (acc, m) => {
      (acc[m.category] ??= []).push(m);
      return acc;
    },
    {} as Record<VehicleModule['category'], VehicleModule[]>,
  );

  const swapRemainingMs = vehicle.workshopUntilAt
    ? Math.max(0, vehicle.workshopUntilAt - gameTime)
    : 0;
  const swapRemainingSec = Math.ceil(swapRemainingMs / 1000);

  return (
    <AnimatePresence>
      <ModalRoot
        onClose={onClose}
        zIndex={Z_MODALS}
        contentClass="flex max-h-[85vh] w-full max-w-3xl flex-col rounded-lg border border-border-strong bg-bg-secondary"
      >
        <header className="flex items-center justify-between border-b border-border-subtle bg-amber-700/15 px-6 py-4">
          <div className="flex items-center gap-3">
            <span className="flex h-9 w-9 items-center justify-center rounded-md bg-amber-600/30 text-amber-200">
              <Wrench className="h-4 w-4" aria-hidden />
            </span>
            <div>
              <p className="text-xs uppercase tracking-widest text-amber-300">Werkstatt</p>
              <h3 className="font-display text-lg font-semibold">
                Modul-Tausch — {vehicle.displayName ?? spec.label}
              </h3>
              <p className="mt-0.5 text-xs text-fg-muted">
                {usedSlots} / {totalSlots} Slots belegt · {free} frei · Tausch +50 %, 5 min außer Dienst
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Schließen"
            className="text-fg-muted transition hover:text-fg-primary"
          >
            <X className="h-4 w-4" aria-hidden />
          </button>
        </header>

        {/* In-progress swap: countdown + cancel */}
        {inWorkshop && (
          <div className="border-b border-amber-500/40 bg-amber-500/10 px-6 py-3 text-amber-100">
            <p className="flex items-center gap-2 text-xs font-semibold">
              <Clock className="h-3.5 w-3.5" aria-hidden /> Tausch läuft — noch {swapRemainingSec} s
            </p>
            <p className="mt-1 text-[11px] opacity-80">
              Fahrzeug ist bis zum Ende des Tauschs nicht einsatzbereit.
            </p>
            <button
              type="button"
              onClick={handleCancel}
              className="mt-2 rounded-md bg-amber-600 px-3 py-1 text-xs font-semibold text-bg-primary transition hover:bg-amber-500"
            >
              Tausch abbrechen ({mode === 'sandbox' ? '0' : '50 %'} Refund)
            </button>
          </div>
        )}

        {/* Workshop missing → block UI */}
        {!hasWorkshop && !inWorkshop && (
          <div className="border-b border-accent-emergency/40 bg-accent-emergency/10 px-6 py-3">
            <p className="flex items-center gap-2 text-xs font-semibold text-accent-emergency">
              <Lock className="h-3.5 w-3.5" aria-hidden /> Werkstatt erforderlich
            </p>
            <p className="mt-1 text-[11px] text-fg-muted">
              An dieser Wache ist keine Werkstatt verfügbar. Erweitere die Wache um die
              Werkstatt-Erweiterung oder verlege das Fahrzeug an eine BF Tier ≥ 4 (eingebaute
              Werkstatt).
            </p>
          </div>
        )}

        {!atBase && hasWorkshop && !inWorkshop && (
          <p className="border-b border-amber-500/40 bg-amber-500/10 px-6 py-2 text-xs text-amber-200">
            Modul-Wechsel nur möglich, wenn das Fahrzeug an der Wache steht.
          </p>
        )}

        <div className="grid flex-1 grid-cols-1 overflow-hidden md:grid-cols-2">
          {/* Installed */}
          <section className="overflow-y-auto border-r border-border-subtle px-5 py-4">
            <p className="mb-3 text-[10px] uppercase tracking-wider text-fg-muted">
              Installiert ({installed.length})
            </p>
            {installed.length === 0 ? (
              <p className="rounded-md border border-dashed border-border-subtle py-6 text-center text-xs text-fg-muted">
                Keine Module installiert.
              </p>
            ) : (
              <ul className="space-y-2">
                {installed.map((inst) => {
                  const m = moduleById(inst.moduleId);
                  if (!m) return null;
                  const isQueued = pendingRemoveIds.includes(inst.id);
                  return (
                    <li
                      key={inst.id}
                      className={[
                        'flex items-start justify-between gap-2 rounded-md border p-3',
                        isQueued
                          ? 'border-accent-emergency/40 bg-accent-emergency/10 line-through opacity-70'
                          : 'border-border-subtle bg-bg-elevated/50',
                      ].join(' ')}
                    >
                      <div className="min-w-0 flex-1">
                        <p className="font-display text-sm font-bold">{m.name}</p>
                        <p className="text-[10px] uppercase tracking-wider text-fg-muted">
                          {CATEGORY_LABEL[m.category]} · {m.slots} Slot
                          {m.slots === 1 ? '' : 's'}
                          {inst.isDefault ? ' · Standard' : ''}
                        </p>
                        {!inst.isDefault && (
                          <p className="text-[10px] text-status-available">
                            -{formatEuro(Math.round(m.cost * MODULE_SWAP_REFUND_RATE))} Refund
                          </p>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          if (isQueued) {
                            setPendingRemoveIds((p) => p.filter((x) => x !== inst.id));
                          } else {
                            setPendingRemoveIds((p) => [...p, inst.id]);
                          }
                        }}
                        disabled={!atBase || !hasWorkshop || inWorkshop}
                        aria-label="Modul ausbauen"
                        className={[
                          'flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md border transition',
                          !atBase || !hasWorkshop || inWorkshop
                            ? 'cursor-not-allowed border-border-subtle text-fg-muted opacity-40'
                            : isQueued
                              ? 'border-accent-emergency text-accent-emergency'
                              : 'border-border-subtle text-fg-muted hover:border-accent-emergency hover:text-accent-emergency',
                        ].join(' ')}
                      >
                        <Trash2 className="h-3.5 w-3.5" aria-hidden />
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
            {/* Pending adds preview */}
            {pendingAddIds.length > 0 && (
              <div className="mt-4">
                <p className="mb-2 text-[10px] uppercase tracking-wider text-accent-dispatch">
                  Eingeplant ({pendingAddIds.length})
                </p>
                <ul className="space-y-2">
                  {pendingAddIds.map((id, i) => {
                    const m = moduleById(id);
                    if (!m) return null;
                    return (
                      <li
                        key={`pending-${id}-${i}`}
                        className="flex items-start justify-between gap-2 rounded-md border border-accent-dispatch/40 bg-accent-dispatch/5 p-3"
                      >
                        <div className="min-w-0 flex-1">
                          <p className="font-display text-sm font-bold">{m.name}</p>
                          <p className="text-[10px] text-accent-dispatch">
                            +{formatEuro(Math.round(m.cost * (1 + MODULE_SWAP_SURCHARGE)))} (inkl. +50 %)
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={() =>
                            setPendingAddIds((p) => p.filter((_, j) => j !== i))
                          }
                          className="rounded-md bg-bg-elevated px-2 py-1 text-[10px] font-semibold text-fg-muted transition hover:text-accent-emergency"
                        >
                          Entfernen
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
          </section>

          {/* Available */}
          <section className="overflow-y-auto px-5 py-4">
            <p className="mb-3 text-[10px] uppercase tracking-wider text-fg-muted">
              Kompatible Module
            </p>
            {compatible.length === 0 ? (
              <p className="rounded-md border border-dashed border-border-subtle py-6 text-center text-xs text-fg-muted">
                Keine kompatiblen Module für diesen Fahrzeugtyp.
              </p>
            ) : (
              <div className="space-y-4">
                {(Object.keys(byCategory) as VehicleModule['category'][]).map((cat) => (
                  <div key={cat}>
                    <p className="mb-1.5 text-[10px] uppercase tracking-wider text-fg-muted">
                      {CATEGORY_LABEL[cat]}
                    </p>
                    <ul className="space-y-1.5">
                      {byCategory[cat].map((m) => {
                        const alreadyProjected = projectedFamilies.has(m.id);
                        const tooLittleSpace = m.slots > free;
                        const surchargeCost = Math.round(
                          m.cost * (1 + MODULE_SWAP_SURCHARGE),
                        );
                        const tooExpensive =
                          mode !== 'sandbox' && credits < netCost + surchargeCost;
                        const blocked = !atBase || !hasWorkshop || inWorkshop;
                        const disabled =
                          blocked || alreadyProjected || tooLittleSpace || tooExpensive;
                        return (
                          <li
                            key={m.id}
                            className={[
                              'flex items-start gap-2 rounded-md border border-border-subtle bg-bg-elevated/30 p-3 transition',
                              disabled
                                ? 'opacity-60'
                                : 'hover:border-accent-dispatch hover:bg-bg-elevated',
                            ].join(' ')}
                          >
                            <div className="min-w-0 flex-1">
                              <p className="font-display text-sm font-bold">{m.name}</p>
                              <p className="mt-0.5 text-xs text-fg-muted">{m.description}</p>
                              <p className="mt-1 text-[10px] uppercase tracking-wider text-fg-muted">
                                {m.slots} Slot{m.slots === 1 ? '' : 's'}
                              </p>
                            </div>
                            <div className="flex flex-shrink-0 flex-col items-end gap-1.5">
                              <span className="font-display text-sm font-bold tabular-nums">
                                {m.cost === 0 ? 'inkl.' : formatEuro(surchargeCost)}
                              </span>
                              <button
                                type="button"
                                onClick={() => setPendingAddIds((p) => [...p, m.id])}
                                disabled={disabled}
                                className={[
                                  'flex items-center gap-1 rounded-md px-2.5 py-1 text-xs font-semibold transition',
                                  disabled
                                    ? 'cursor-not-allowed bg-bg-elevated text-fg-muted'
                                    : 'bg-accent-dispatch text-white hover:bg-accent-dispatch-hover',
                                ].join(' ')}
                              >
                                <Plus className="h-3 w-3" aria-hidden />
                                {alreadyProjected
                                  ? 'Belegt'
                                  : tooLittleSpace
                                    ? 'Voll'
                                    : tooExpensive
                                      ? 'Zu teuer'
                                      : 'Einplanen'}
                              </button>
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                ))}
              </div>
            )}
          </section>
        </div>

        {/* Footer with start-swap button */}
        <footer className="flex items-center justify-between gap-3 border-t border-border-subtle bg-bg-secondary px-6 py-3">
          <div className="text-[11px] text-fg-muted">
            <Boxes className="mr-1 inline h-3 w-3" aria-hidden />
            {hasChanges ? (
              <>
                Tausch:{' '}
                <span className="font-semibold text-fg-primary">
                  +{formatEuro(addCostWithSurcharge)}
                </span>
                {removeRefund > 0 && (
                  <>
                    {' / '}
                    <span className="text-status-available">-{formatEuro(removeRefund)} Refund</span>
                  </>
                )}
                {' '}={' '}
                <span className="font-display text-base font-bold text-accent-emergency">
                  {formatEuro(netCost)}
                </span>
                {' · '}
                Dauer {Math.round(MODULE_SWAP_DURATION_MS / 60_000)} min
              </>
            ) : (
              'Keine Änderungen eingeplant.'
            )}
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-border-subtle bg-bg-elevated px-3 py-2 text-xs font-semibold text-fg-secondary transition hover:bg-bg-elevated/70"
            >
              Schließen
            </button>
            <button
              type="button"
              onClick={handleStart}
              disabled={!hasChanges || !atBase || !hasWorkshop || inWorkshop}
              className={[
                'flex items-center gap-1.5 rounded-md px-4 py-2 text-sm font-semibold text-white transition',
                hasChanges && atBase && hasWorkshop && !inWorkshop
                  ? 'bg-amber-600 hover:bg-amber-500'
                  : 'cursor-not-allowed bg-bg-elevated text-fg-muted',
              ].join(' ')}
            >
              <Wrench className="h-3.5 w-3.5" aria-hidden />
              Tausch starten
            </button>
          </div>
        </footer>
      </ModalRoot>
    </AnimatePresence>
  );
}
